/**
 * Google People API (read-only) connector. Pulls contacts with phones,
 * emails, photos, birthdays, anniversaries.
 *
 * Dial uses these as the relationship-graph seed. We never store contacts
 * server-side -- everything lives in OPFS, encrypted via passkey-derived KEK.
 */
import type { ContactSeed } from './types.ts';
import { MOCK_CONTACTS } from './mock-data.ts';
import { ensureFreshToken, gmailFetch, type OAuthToken, GmailOAuth } from './gmail-oauth.ts';

const LIST_URL = 'https://people.googleapis.com/v1/people/me/connections';
const PERSON_FIELDS =
  'names,emailAddresses,phoneNumbers,photos,birthdays,events,relations,organizations';

interface GPerson {
  resourceName: string;
  names?: ReadonlyArray<{ displayName?: string; givenName?: string; familyName?: string }>;
  emailAddresses?: ReadonlyArray<{ value: string; type?: string }>;
  phoneNumbers?: ReadonlyArray<{ value: string; canonicalForm?: string; type?: string }>;
  photos?: ReadonlyArray<{ url: string; default?: boolean }>;
  birthdays?: ReadonlyArray<{ date?: { year?: number; month: number; day: number } }>;
  events?: ReadonlyArray<{ type?: string; date?: { year?: number; month: number; day: number } }>;
  relations?: ReadonlyArray<{ person?: string; type?: string }>;
  organizations?: ReadonlyArray<{ name?: string; title?: string }>;
}
interface PeopleResp {
  connections?: GPerson[];
  nextPageToken?: string;
  totalItems?: number;
}

export interface GcontactsOpts {
  readonly mode: 'mock' | 'live';
  readonly oauth?: GmailOAuth;
  readonly token?: OAuthToken;
  readonly max_results?: number;
}

export async function fetchContacts(opts: GcontactsOpts): Promise<readonly ContactSeed[]> {
  if (opts.mode === 'mock') return MOCK_CONTACTS;
  if (!opts.oauth || !opts.token) throw new Error('live mode requires oauth + token');

  const token = await ensureFreshToken(opts.oauth, opts.token);
  const limit = opts.max_results ?? 1000;
  const out: ContactSeed[] = [];

  let pageToken: string | undefined;
  while (out.length < limit) {
    const u = new URL(LIST_URL);
    u.searchParams.set('personFields', PERSON_FIELDS);
    u.searchParams.set('pageSize', String(Math.min(1000, limit - out.length)));
    if (pageToken) u.searchParams.set('pageToken', pageToken);
    const r = await gmailFetch<PeopleResp>(token, u.toString());
    for (const p of r.connections ?? []) {
      const seed = toSeed(p);
      if (seed) out.push(seed);
    }
    pageToken = r.nextPageToken;
    if (!pageToken) break;
  }
  return out;
}

function toSeed(p: GPerson): ContactSeed | null {
  const name = p.names?.[0]?.displayName?.trim();
  if (!name) return null;
  const phones = (p.phoneNumbers ?? []).map((x) => x.canonicalForm ?? x.value).filter(Boolean);
  const emails = (p.emailAddresses ?? []).map((x) => x.value).filter(Boolean);
  const photo = p.photos?.find((x) => !x.default)?.url;
  const bday = p.birthdays?.[0]?.date;
  const anniv = p.events?.find((e) => e.type?.toLowerCase().includes('anniversary'))?.date;
  const org = p.organizations?.[0]?.name;
  const relation_hint = org ? 'work' : undefined;

  const seed: ContactSeed = {
    external_id: p.resourceName,
    name,
    phones,
    emails,
    ...(photo ? { photo_url: photo } : {}),
    ...(bday ? { birthday: bday.year ? `${bday.year}-${pad(bday.month)}-${pad(bday.day)}` : `--${pad(bday.month)}-${pad(bday.day)}` } : {}),
    ...(anniv ? { anniversary: anniv.year ? `${anniv.year}-${pad(anniv.month)}-${pad(anniv.day)}` : `--${pad(anniv.month)}-${pad(anniv.day)}` } : {}),
    ...(relation_hint ? { relation_hint } : {}),
    source: 'gcontacts',
    provenance: { connector: 'gcontacts', source_id: p.resourceName, fetched_at: Date.now() },
  };
  return seed;
}

function pad(n: number): string { return String(n).padStart(2, '0'); }
